import { useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { Alert, Badge, Button, Loading } from '../components/ui';
import { UsageMeter, includedSummary, resetDate } from '../lib/usage';

interface UsageResp { meters: { deep_research: UsageMeter; lead_source: UsageMeter } }

export default function Usage() {
  const { me } = useAuth();
  const canBuy = me?.role === 'owner';
  const { data, loading, error } = useAsync<UsageResp>(() => apiGet('/api/usage'), []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="h-display text-3xl text-soft">Usage</h1>
        <p className="mt-1 text-sm text-muted">
          Your monthly included allowances for the two features that cost per run — Deep Research and Lead Score sourcing — plus any prepaid top-ups. Allowances reset on your billing date.
        </p>
      </div>

      {loading ? (
        <Loading label="Loading your usage…" />
      ) : error ? (
        <Alert>{error}</Alert>
      ) : data ? (
        <div className="grid gap-4 md:grid-cols-2">
          <MeterCard meter={data.meters.deep_research} canBuy={canBuy} />
          <MeterCard meter={data.meters.lead_source} canBuy={canBuy} />
        </div>
      ) : null}

      {!canBuy && (
        <p className="text-xs text-muted">Only the workspace owner can buy top-up packs.</p>
      )}
    </div>
  );
}

function MeterCard({ meter, canBuy }: { meter: UsageMeter; canBuy: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pct = meter.included > 0 ? Math.min(100, Math.round((meter.usedIncluded / meter.included) * 100)) : 100;
  const low = meter.remaining <= Math.max(3, Math.round(meter.included * 0.1));
  const barColor = meter.remaining === 0 ? 'bg-danger' : low ? 'bg-victory' : 'bg-electric';

  async function buy() {
    setBusy(true); setErr(null);
    try {
      const r = await apiPost<{ url: string }>('/api/usage/topup', { meter: meter.meter });
      if (r.url) window.location.href = r.url;
      else { setErr('Could not start checkout.'); setBusy(false); }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start checkout.');
      setBusy(false);
    }
  }

  const blockedNoEntitlement = meter.requiresEntitlement && !meter.entitled;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-head text-lg font-semibold text-soft">{meter.label}</h2>
            {meter.isFirstCycle && meter.included > 0 && <Badge className="border-victory/40 text-victory">first month</Badge>}
          </div>
          <div className="mt-0.5 text-sm text-muted">{includedSummary(meter)}</div>
        </div>
        <div className="text-right">
          <div className="font-head text-3xl leading-none text-soft">{meter.remaining}</div>
          <div className="text-xs text-muted">{meter.unitPlural} left</div>
        </div>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-surface-3">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-3 space-y-1 text-xs text-muted">
        {meter.topupRemaining > 0 && <div>+ {meter.topupRemaining} top-up {meter.unitPlural} (don't expire)</div>}
        {!blockedNoEntitlement && <div>Included resets {resetDate(meter.nextReset)}</div>}
        {blockedNoEntitlement && (
          <div className="text-victory">Unlock any tool to get {meter.ongoing} included {meter.unitPlural}/month.</div>
        )}
      </div>

      {canBuy && (
        <div className="mt-4 flex items-center gap-3">
          <Button variant={low ? 'primary' : 'ghost'} loading={busy} onClick={buy} className="text-sm">
            Buy top-up — {meter.topup.label}
          </Button>
          {low && meter.remaining > 0 && <span className="text-xs text-victory">Running low</span>}
        </div>
      )}
      {err && <div className="mt-3"><Alert>{err}</Alert></div>}
    </div>
  );
}
